import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = '/api';

interface ApiloNote {
  id: number | string | null;
  type: number | null;
  comment: string;
  created_at: string | null;
  source?: 'apilo' | 'local';
  author_name?: string | null;
}

// Inline panel z wiadomosciami Apilo (orderNotes) + NASZE lokalne adnotacje - renderowany POD przyciskami
// w Przesylkach/Zwrotach. orderId = id_external (UUID). Pole dodawania zapisuje lokalnie (NIE do Apilo).
export default function ApiloNotesPanel({ orderId }: { orderId: string }) {
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<ApiloNote[] | null>(null);
  const [errMsg, setErrMsg] = useState('');
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [syncMsg, setSyncMsg] = useState('');
  const [statusMap, setStatusMap] = useState<{id:number,name:string}[]>([]);
  const [curStatus, setCurStatus] = useState<number | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [doc, setDoc] = useState<{has_document:boolean,type_name?:string|null,number?:string|null} | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrMsg('');
    setNotes(null);
    setCurStatus(null);
    try {
      const res = await axios.get(`${API}/apilo/order-notes?order_id=${encodeURIComponent(orderId)}`);
      const status = res.data?.status;
      const list: ApiloNote[] = res.data?.notes || [];
      if (status === 'not_configured') {
        setErrMsg('Brak konfiguracji Apilo dla tej firmy.');
      } else if (status === 'not_found') {
        setErrMsg('Nie znaleziono tego zam\u00f3wienia w Apilo.');
      } else if (status === 'api_error') {
        setErrMsg('Apilo chwilowo niedost\u0119pne. Spr\u00f3buj ponownie za chwil\u0119.');
      } else {
        setNotes(list);
        const sid = res.data?.order_status_id;
        setCurStatus(typeof sid === 'number' ? sid : null);
      }
    } catch (e) {
      setErrMsg('B\u0142\u0105d pobierania wiadomo\u015bci z Apilo.');
    }
    setLoading(false);
  }, [orderId]);

  useEffect(() => { load(); }, [load]);
  // status + dokument sprzedazy (rownolegle z notatkami)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [sm, od] = await Promise.all([
          axios.get(`${API}/apilo/order-status-map`),
          axios.get(`${API}/apilo/order-document?order_id=${encodeURIComponent(orderId)}`),
        ]);
        if (!alive) return;
        setStatusMap(sm.data?.statuses || []);
        setDoc(od.data || null);
      } catch (e) { if (alive) { setStatusMap([]); setDoc(null); } }
    })();
    return () => { alive = false; };
  }, [orderId]);
  const changeStatus = async (sid: number) => {
    setStatusSaving(true); setStatusMsg('');
    try {
      await axios.put(`${API}/apilo/order-status`, { order_id: orderId, status_id: sid });
      setCurStatus(sid);
      setStatusMsg('\u2713 Status zmieniony');
      setTimeout(() => setStatusMsg(''), 3000);
    } catch (e:any) {
      setStatusMsg('Nie uda\u0142o si\u0119 zmieni\u0107 statusu');
    }
    setStatusSaving(false);
  };

  const addNote = async () => {
    const text = draft.trim();
    if (!text || saving) return;
    setSaving(true);
    setSaveErr('');
    try {
      const _resp = await axios.post(`${API}/apilo/order-notes/local`, { order_id: orderId, comment: text });
      setDraft('');
      const _synced = _resp && _resp.data && _resp.data.apilo_synced;
      setSyncMsg(_synced ? '\u2713 Zapisano w CRM i wys\u0142ano do Apilo' : '\u2713 Zapisano w CRM (nie wys\u0142ano do Apilo)');
      setTimeout(() => setSyncMsg(''), 4000);
      await load();
    } catch (e) {
      setSaveErr('Nie uda\u0142o si\u0119 zapisa\u0107 adnotacji.');
    }
    setSaving(false);
  };

  const fmtDate = (d: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString('pl-PL'); } catch { return d; }
  };

  return (
    <div style={{ padding: 12 }}>
      {/* status zamowienia + dokument sprzedazy */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid #ede9fe' }}>
        {statusMap.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>Status Apilo:</span>
            <select value={curStatus ?? ''} onChange={e => changeStatus(Number(e.target.value))} disabled={statusSaving}
              style={{ fontSize: 12, padding: '3px 8px', borderRadius: 6, border: '1px solid #ddd6fe', background: '#fff', color: '#4c1d95', cursor: 'pointer' }}>
              <option value="" disabled>{statusSaving ? 'Zapisuj\u0119\u2026' : 'wybierz status\u2026'}</option>
              {statusMap.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              {curStatus !== null && !statusMap.some(s => s.id === curStatus) && (
                <option value={curStatus}>Status #{curStatus}</option>
              )}
            </select>
            {statusMsg && <span style={{ fontSize: 11, color: statusMsg.startsWith('\u2713') ? '#059669' : '#dc2626' }}>{statusMsg}</span>}
          </div>
        )}
        {doc && (
          <div style={{ fontSize: 12, color: '#4c1d95', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 6, padding: '3px 8px' }}>
            {doc.has_document ? ('\uD83D\uDCC4 ' + (doc.type_name || 'Dokument') + (doc.number ? ' ' + doc.number : '')) : '\uD83D\uDCC4 Brak dokumentu sprzeda\u017cy'}
          </div>
        )}
      </div>
      {/* pole dodawania naszej adnotacji */}
      <div style={{ marginBottom: 12 }}>
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder={"Dodaj w\u0142asn\u0105 adnotacj\u0119 do tego zam\u00f3wienia\u2026"}
          rows={2}
          style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd6fe', resize: 'vertical', fontFamily: 'inherit' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
          <button
            onClick={addNote}
            disabled={saving || !draft.trim()}
            style={{ fontSize: 13, fontWeight: 600, padding: '7px 16px', borderRadius: 8, border: 'none', background: (saving || !draft.trim()) ? '#c4b5fd' : '#6d28d9', color: '#fff', cursor: (saving || !draft.trim()) ? 'default' : 'pointer' }}
          >
            {saving ? '\u23f3 Zapisuj\u0119\u2026' : '\u002b Dodaj adnotacj\u0119'}
          </button>
          {saveErr && <span style={{ fontSize: 12, color: '#dc2626' }}>{saveErr}</span>}
                  {syncMsg && <span style={{ fontSize: 12, color: '#059669' }}>{syncMsg}</span>}
        </div>
      </div>

      {loading && <div style={{ fontSize: 13, color: '#6b7280', padding: '14px 0', textAlign: 'center' }}>{'\u0141adowanie\u2026'}</div>}
      {!loading && errMsg && (
        <div style={{ fontSize: 14, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 14px' }}>{errMsg}</div>
      )}
      {!loading && !errMsg && notes && notes.length === 0 && (
        <div style={{ fontSize: 14, color: '#6b7280', padding: '14px 0', textAlign: 'center' }}>{"Brak wiadomo\u015bci. Dodaj pierwsz\u0105 adnotacj\u0119 powy\u017cej."}</div>
      )}
      {!loading && !errMsg && notes && notes.length > 0 && notes.map((n, i) => {
        const isLocal = n.source === 'local';
        return (
          <div key={n.id || i} style={{ border: '1px solid ' + (isLocal ? '#ddd6fe' : '#e5e7eb'), borderRadius: 8, padding: '10px 12px', marginBottom: 8, background: isLocal ? '#f5f3ff' : '#fff' }}>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {isLocal && <span style={{ fontSize: 10, fontWeight: 600, color: '#6d28d9', background: '#ede9fe', padding: '1px 7px', borderRadius: 10 }}>{"\ud83d\udcdd Twoja adnotacja"}</span>}
              <span>{fmtDate(n.created_at)}</span>
              {isLocal && n.author_name && <span>{"\u2022 " + n.author_name}</span>}
            </div>
            <div style={{ fontSize: 13, color: '#111827', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{n.comment || '\u2014'}</div>
          </div>
        );
      })}
    </div>
  );
}
